import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { useTheme } from "@emotion/react";
import moment from "moment";
import { listInstanceLogs, listQueueJobs } from "api/whatsapp";
import { getAllCustomerGroups } from "api/customerGroup";

const SEVERITY_COLORS = { info: "info", warning: "warning", error: "error" };

const EVENT_TYPES = [
  "disconnected",
  "auth_failure",
  "client_error",
  "init_error",
  "restart_manual",
  "auto_restart",
  "auto_restart_failed",
  "restart_failed",
  "failure_threshold",
  "jobs_requeued",
  "health_check_failed",
  "escalated",
  "recovered",
];

const FAILURE_CATEGORIES = [
  "group_unavailable",
  "invalid_destination",
  "provider_timeout",
  "provider_error",
  "unknown",
];

const MESSAGE_TYPE_LABELS = {
  arrival_notification: "Notify",
  delivery_notification: "Delivery",
  agent_notification: "Agent",
  manual_message: "Manual",
  bulk_message: "Bulk",
  system_message: "System",
};

const WhatsAppLogs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // "instance" — instance lifecycle events; "failures" — message send failures
  const [view, setView] = useState("instance");
  const [groups, setGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Shared filters
  const [filterGroup, setFilterGroup] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [limit, setLimit] = useState(100);

  // Instance-logs-only filters
  const [filterEventType, setFilterEventType] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");

  // Message-failures-only filters
  const [filterCategory, setFilterCategory] = useState("");

  const [detail, setDetail] = useState(null);

  const groupName = (groupId) => {
    if (!groupId) return "-";
    return groups.find((g) => g.id === groupId)?.name || groupId;
  };

  useEffect(() => {
    getAllCustomerGroups().then((res) => {
      if (res.success && Array.isArray(res.data)) setGroups(res.data);
    });
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    if (view === "instance") {
      const res = await listInstanceLogs({
        groupId: filterGroup || undefined,
        eventType: filterEventType || undefined,
        severity: filterSeverity || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        limit,
      });
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
    } else {
      const res = await listQueueJobs({
        failedOnly: true,
        groupId: filterGroup || undefined,
        failureReasonCategory: filterCategory || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        limit,
      });
      setRows(res.success && Array.isArray(res.data) ? res.data : []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, filterGroup, filterEventType, filterSeverity, filterCategory, fromDate, toDate, limit]);

  const resetFilters = () => {
    setFilterGroup("");
    setFilterEventType("");
    setFilterSeverity("");
    setFilterCategory("");
    setFromDate("");
    setToDate("");
  };

  const instanceColumns = [
    {
      field: "createdAt",
      headerName: "Time",
      width: 160,
      valueGetter: (params) => params.row.createdAt,
      renderCell: ({ value }) => (value ? moment(value).format("DD/MM/YY HH:mm:ss") : "-"),
    },
    {
      field: "groupName",
      headerName: "Group",
      width: 160,
      valueGetter: (params) => groupName(params.row.groupId),
    },
    {
      field: "eventType",
      headerName: "Event",
      width: 170,
      renderCell: ({ value }) => <Chip label={value} size="small" variant="outlined" />,
    },
    {
      field: "severity",
      headerName: "Severity",
      width: 100,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" color={SEVERITY_COLORS[value] || "default"} />
      ),
    },
    {
      field: "detail",
      headerName: "Detail",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => params.row.detail || "-",
    },
  ];

  const failureColumns = [
    {
      field: "queuedAt",
      headerName: "Time",
      width: 160,
      valueGetter: (params) => params.row.schedule?.failedAt || params.row.schedule?.queuedAt || params.row.createdAt,
      renderCell: ({ value }) => (value ? moment(value).format("DD/MM/YY HH:mm:ss") : "-"),
    },
    {
      field: "group",
      headerName: "Group",
      width: 150,
      valueGetter: (params) => groupName(params.row.provider?.selectedGroupId),
    },
    {
      field: "phoneNumber",
      headerName: "Phone",
      width: 140,
      valueGetter: (params) => params.row.destination?.phoneNumber || "-",
    },
    {
      field: "messageType",
      headerName: "Type",
      width: 110,
      valueGetter: (params) => MESSAGE_TYPE_LABELS[params.row.messageType] || params.row.messageType,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ value }) => <Chip label={value} size="small" variant="outlined" />,
    },
    {
      field: "failureReasonCategory",
      headerName: "Reason",
      width: 150,
      valueGetter: (params) => params.row.result?.failureReasonCategory || "-",
    },
    {
      field: "attempts",
      headerName: "Attempts",
      width: 90,
      valueGetter: (params) => {
        const a = params.row.attempts;
        return a ? `${a.current}/${a.max} (${a.total ?? "-"} lifetime)` : "-";
      },
    },
    {
      field: "lastErrorMessage",
      headerName: "Last Error",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => params.row.result?.lastErrorMessage || "-",
    },
  ];

  return (
    <Box m="0 0rem">
      <Grid container spacing={2} mt={1} mb={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            color="secondary"
            onChange={(e, v) => v && setView(v)}
            fullWidth
          >
            <ToggleButton value="instance">Instance Logs</ToggleButton>
            <ToggleButton value="failures">Message Failures</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Group</InputLabel>
            <Select value={filterGroup} label="Group" onChange={(e) => setFilterGroup(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {view === "instance" ? (
          <>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Event</InputLabel>
                <Select value={filterEventType} label="Event" onChange={(e) => setFilterEventType(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {EVENT_TYPES.map((et) => (
                    <MenuItem key={et} value={et}>
                      {et}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity</InputLabel>
                <Select value={filterSeverity} label="Severity" onChange={(e) => setFilterSeverity(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {["info", "warning", "error"].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        ) : (
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Reason</InputLabel>
              <Select value={filterCategory} label="Reason" onChange={(e) => setFilterCategory(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {FAILURE_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="From"
            type="datetime-local"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            size="small"
            label="To"
            type="datetime-local"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Last</InputLabel>
            <Select value={limit} label="Last" onChange={(e) => setLimit(e.target.value)}>
              {[50, 100, 500, 1000].map((n) => (
                <MenuItem key={n} value={n}>
                  {n} entries
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={1}>
          <Chip label="Reset" size="small" variant="outlined" onClick={resetFilters} clickable />
        </Grid>
      </Grid>

      {isMobile ? (
        <Box>
          {isLoading && rows.length === 0 && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && rows.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No entries found
            </Typography>
          )}
          <Stack spacing={1}>
            {rows.map((row) => (
              <Card
                key={row._id || row.id}
                variant="outlined"
                onClick={() => setDetail(row)}
                sx={{ cursor: "pointer" }}
              >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  {view === "instance" ? (
                    <>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                        <Chip label={row.eventType} size="small" variant="outlined" />
                        <Chip label={row.severity} size="small" color={SEVERITY_COLORS[row.severity] || "default"} />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {groupName(row.groupId)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.createdAt ? moment(row.createdAt).format("DD/MM/YY HH:mm:ss") : "-"}
                      </Typography>
                      {row.detail && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {row.detail}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                        <Chip label={MESSAGE_TYPE_LABELS[row.messageType] || row.messageType} size="small" variant="outlined" />
                        <Chip label={row.status} size="small" variant="outlined" />
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {groupName(row.provider?.selectedGroupId)} — {row.destination?.phoneNumber || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {(row.schedule?.failedAt || row.schedule?.queuedAt) &&
                          moment(row.schedule?.failedAt || row.schedule?.queuedAt).format("DD/MM/YY HH:mm:ss")}
                      </Typography>
                      {row.result?.lastErrorMessage && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          {row.result.lastErrorMessage}
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box sx={{ height: 650 }}>
          <StyledDataGrid
            rows={rows}
            columns={view === "instance" ? instanceColumns : failureColumns}
            getRowId={(row) => row._id || row.id}
            loading={isLoading}
            onRowClick={(params) => setDetail(params.row)}
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {view === "instance" ? "Instance Event Detail" : "Message Failure Detail"}
          {detail && view === "instance" && (
            <Chip label={detail.severity} size="small" color={SEVERITY_COLORS[detail.severity] || "default"} sx={{ ml: 1 }} />
          )}
          {detail && view === "failures" && (
            <Chip label={detail.status} size="small" variant="outlined" sx={{ ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detail && view === "instance" && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Group</Typography>
                <Typography variant="body2">{groupName(detail.groupId)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Event</Typography>
                <Typography variant="body2">{detail.eventType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Time</Typography>
                <Typography variant="body2">
                  {detail.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY HH:mm:ss") : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Detail</Typography>
                <Typography variant="body2">{detail.detail || "-"}</Typography>
              </Grid>
              {detail.meta && Object.keys(detail.meta).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Meta</Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      bgcolor: "background.default",
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      overflow: "auto",
                    }}
                  >
                    {JSON.stringify(detail.meta, null, 2)}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
          {detail && view === "failures" && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Job ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                  {detail.id}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Group</Typography>
                <Typography variant="body2">{groupName(detail.provider?.selectedGroupId)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{detail.destination?.phoneNumber || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Attempts</Typography>
                <Typography variant="body2">
                  {detail.attempts
                    ? `${detail.attempts.current}/${detail.attempts.max} this episode, ${detail.attempts.total ?? "-"} lifetime`
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Message</Typography>
                <Box
                  sx={{ mt: 0.5, p: 1.5, bgcolor: "background.default", borderRadius: 1, whiteSpace: "pre-wrap" }}
                >
                  <Typography variant="body2">{detail.content?.text || "-"}</Typography>
                </Box>
              </Grid>
              {detail.result?.failureReasonCategory && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Failure Reason</Typography>
                  <Typography variant="body2">{detail.result.failureReasonCategory}</Typography>
                </Grid>
              )}
              {detail.result?.lastErrorMessage && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Last Error</Typography>
                  <Typography variant="body2" color="error">{detail.result.lastErrorMessage}</Typography>
                </Grid>
              )}
              {detail.attempts?.history?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Attempt History</Typography>
                  {detail.attempts.history.map((h, i) => (
                    <Box
                      key={i}
                      sx={{ mt: 0.5, p: 1, bgcolor: "background.default", borderRadius: 1, fontSize: "0.8rem" }}
                    >
                      <Typography variant="body2">
                        <strong>#{h.attemptNumber}</strong> — {h.outcome}{" "}
                        {h.startedAt && `at ${moment(h.startedAt).format("DD/MM HH:mm:ss")}`}
                      </Typography>
                      {h.errorMessage && (
                        <Typography variant="body2" color="error">
                          {h.errorMessage}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WhatsAppLogs;
